import React, { useState } from "react";
import IngredientSearch from "./IngredientSearch";
import StoredIngredients from "./StoredIngredients";
import { supabase } from "../supabaseClient";
import { useIngredients } from "../context/IngredientsProvider";
import { FaSort, FaTrashAlt } from "react-icons/fa";
import "../styles/Pantry.css";

interface PantryProps {
  session: any;
}

const Pantry: React.FC<PantryProps> = ({ session }) => {
  const { selectedIngredients, setSelectedIngredients } = useIngredients();
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [showGuestInfo, setShowGuestInfo] = useState(false);
  const [guestNotified, setGuestNotified] = useState(false);
  const [sortOption, setSortOption] = useState<"alphabetical" | "category">(
    "category"
  );
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const addIngredient = async (ingredient: {
    label: string;
    value: string;
  }) => {
    const alreadyExists = selectedIngredients.some(
      (item) => item.value === ingredient.value
    );
    if (alreadyExists) {
      setShowDuplicateWarning(true);
      setTimeout(() => setShowDuplicateWarning(false), 3000);
      return;
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user && !guestNotified) {
        setShowGuestInfo(true);
        setGuestNotified(true);
        setTimeout(() => setShowGuestInfo(false), 4000);
      }

      if (user) {
        const { error } = await supabase.from("user_ingredients").insert([
          {
            label: ingredient.label,
            value: ingredient.value,
            user_id: user.id,
          },
        ]);
        if (error) {
          console.error("Virhe tallennettaessa raaka-ainetta:", error);
        }
      }

      setSelectedIngredients((prev) => [...prev, ingredient]);
    } catch (error) {
      console.error("Virhe tallennettaessa raaka-ainetta:", error);
    }
  };

  const handleClearAll = () => {
    setShowClearConfirm(true);
  };

  const handleConfirmClear = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (user) {
      const { error } = await supabase
        .from("user_ingredients")
        .delete()
        .eq("user_id", user.id);
      if (error) {
        console.error("Virhe poistettaessa kaikki:", error);
      }
    }

    setSelectedIngredients([]);
    setShowClearConfirm(false);
  };

  const handleCancelClear = () => {
    setShowClearConfirm(false);
  };

  return (
    <div className="food-storage-wrapper">
      {/* 🔔 Modal suoraan wrapperin sisällä */}
      {showClearConfirm && (
        <div className="clear-confirm-modal">
          <p>Poistetaanko kaikki raaka-aineet ruokakomerostasi?</p>
          <div className="modal-buttons">
            <button className="confirm" onClick={handleConfirmClear}>
              Kyllä
            </button>
            <button className="cancel" onClick={handleCancelClear}>
              Peruuta
            </button>
          </div>
        </div>
      )}

      <div className="food-storage-container">
        <div className="ingredient-section">
          <div className="ingredient-search-container">
            <IngredientSearch onAddIngredient={addIngredient} />
          </div>

          {showDuplicateWarning && (
            <div className="floating-warning">
              Ruokakomerossa on jo tämä raaka-aine.
            </div>
          )}

          {showGuestInfo && (
            <div className="floating-warning">
              Raaka-aineet poistuvat, kun suljet tai päivität selaimen. Kirjaudu
              sisään tallentaaksesi ne pysyvästi.
            </div>
          )}

          <StoredIngredients session={session} sortOption={sortOption} />
        </div>

        {/* 🔘 Leijuvat napit */}
        <div className="sorting-button-overlay">
          <FaSort
            className="sort-icon"
            onClick={() =>
              setSortOption(
                sortOption === "alphabetical" ? "category" : "alphabetical"
              )
            }
            aria-label="Vaihda lajittelua"
            title="Vaihda lajittelua"
          />
          <FaTrashAlt
            className="sort-icon"
            onClick={handleClearAll}
            aria-label="Tyhjennä ruokakomero"
            title="Tyhjennä kaikki"
          />
          <div className="floating-label">RUOKAVARASTO</div>
        </div>
      </div>
    </div>
  );
};

export default Pantry;
